import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { stockUnitsApi } from '../stockApi';
import type { StockUnitListItem } from '../stockTypes';

/**
 * Put a stolen unit that turned up back on the owned tally.
 *
 * **The other half of the write-off, and it carries the SAME key.** Restore, activate and
 * unlock take no ceremony because they put a RECORD back; this moves STOCK, and deciding what
 * is on the owned tally is one privilege in both directions.
 *
 * **An adjustment, never a receipt.** Nothing arrived — the thing was here all along and the
 * tally was wrong. A receipt would put a second acquisition in the ledger for one purchase, and
 * a receipts report would then count it twice.
 *
 * It still gates its primary action behind the confirm tick, because it writes. What it does
 * not take is the password: that safeguard is for destroying and revoking, and this is the
 * opposite.
 */
export function ReturnStockUnitModal({
  open,
  unit,
  onClose,
  onSaved,
}: {
  open: boolean;
  unit: StockUnitListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Optional, unlike a write-off's. Putting something back is the undo, and demanding a
  // justification for an undo only teaches readers to type anything into the box.
  const [reason, setReason] = useState('');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await stockUnitsApi.returnToStock(unit.id, { reason: reason.trim() || null });

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not return this unit.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'return-unit' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Return to stock"
      size="md"
      id="return-unit-modal"
      confirm={{
        id: 'return-unit-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="return-unit-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="return-unit-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Serial', value: unit.serialNumber, mono: true },
            { label: 'Item', value: `${unit.sku} — ${unit.itemName}` },
            { label: 'Facility', value: unit.storageFacilityName },
          ]}
        />

        <p className="text-sm text-slate-600">
          This puts the unit back on the owned tally and records an adjustment on the ledger — not
          a receipt, because nothing arrived. The write-off stays on the record: the history says
          it was reported missing and then found.
        </p>

        <TextField
          id="return-unit-reason"
          label="Where was it?"
          description="Optional, and recorded on the ledger line."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('reason')}
        />
      </div>
    </Modal>
  );
}
